import { type FC, useCallback, useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import toast from "react-hot-toast";

import Modal from "../../modal/Modal";
import Button from "../../button/Button";
import InlineLoading from "../../loading/InlineLoading";
import DateDisplay, { DATE_DISPLAY_TYPE } from "../../date-display/DateDisplay";
import TranscriptionOptionsForm, {
  DEFAULT_TRANSCRIPTION_OPTIONS,
  toTranscriptionOptions,
  type TranscriptionOptionsValue,
} from "../../transcription/TranscriptionOptionsForm";
import { formatTimecode } from "../../../utils/timecode";
import {
  useGetTranscriptionQuery,
  useStartTranscriptionMutation,
  type TranscriptJson,
  type TranscriptSegment,
} from "../../../app/services/mediaTranscription";

/**
 * メディア文字起こしモーダル。
 *
 * メディアプレビューの「…」メニュー →「文字起こし」から開く。
 * - 未作成: 音声の種類（会話 / 歌唱・楽曲）とオプション付きの開始画面
 * - 処理中: ポーリングで完了を検知（15 秒間隔、5 分経過後は 60 秒に緩和）
 * - 完了: タイムスタンプ付きセグメント一覧。行クリックで再生位置へシーク、
 *   全文コピー / テキスト書き出し / 再実行
 * - 失敗: エラー表示 + 再実行
 *
 * ポーリングは MediaDetailModal の ACR パターンと同じ「状態に応じた
 * 条件付き pollingInterval」方式（docs/api/media-transcription.md）。
 */

export interface MediaTranscriptionModalProps {
  /** 対象メディアの fileId（バックエンドの横断レジストリで解決される） */
  fileId: string;
  /** モーダルタイトルに出すファイル名 */
  fileName?: string;
  onClose: () => void;
  /** セグメント行クリック時に再生位置（秒）へシークする */
  onSeek?: (seconds: number) => void;
}

/** 処理中ステータスの自動更新間隔（開始直後） */
const FAST_POLL_INTERVAL_MS = 15 * 1000;

/** fast ポーリングを続ける時間。超過後は slow 間隔に落として追従する */
const FAST_POLL_DURATION_MS = 5 * 60 * 1000;

/** 長時間処理中が続く場合の自動更新間隔 */
const SLOW_POLL_INTERVAL_MS = 60 * 1000;

/** 秒を mm:ss（1 時間以上は h:mm:ss）に整形する */
const formatTimestamp = (seconds: number): string => {
  const total = Math.max(0, Math.floor(seconds));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (h > 0) {
    return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  }
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
};

/** "spk_0" → "話者1" のような表示ラベルに変換する */
const speakerLabel = (speaker: string | null): string | null => {
  if (!speaker) return null;
  const match = speaker.match(/(\d+)$/);
  if (!match) return speaker;
  return `話者${Number(match[1]) + 1}`;
};

// ─── styled-components ─────────────────────────────────

const CenterState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 24px 0;
  text-align: center;
`;

const StateText = styled.div.attrs({
  className: "bodytext-13 u-line-height-150",
})`
  color: var(--soremo-gray);
  white-space: pre-line;
`;

const StateSubText = styled.div.attrs({
  className: "bodytext-11 u-line-height-150",
})`
  color: var(--soremo-light-gray);
  white-space: pre-line;
`;

const SegmentList = styled.div`
  display: flex;
  flex-direction: column;
`;

const SegmentRow = styled.div<{ $clickable: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 6px 4px;
  border-bottom: 1px solid #f0f0f0;
  cursor: ${(props) => (props.$clickable ? "pointer" : "default")};

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background-color: ${(props) =>
      props.$clickable ? "var(--soremo-background)" : "transparent"};
  }
`;

const SegmentTime = styled.span.attrs({
  className: "bodytext-11 u-line-height-150",
})`
  flex-shrink: 0;
  min-width: 44px;
  color: var(--soremo-blue);
  font-variant-numeric: tabular-nums;
`;

const SegmentSpeaker = styled.span.attrs({
  className: "label8 u-line-height-150",
})`
  flex-shrink: 0;
  color: var(--soremo-light-gray);
  min-width: 36px;
`;

const SegmentText = styled.span.attrs({
  className: "bodytext-13 u-line-height-150",
})`
  color: var(--soremo-black);
  word-break: break-word;
`;

const ResultMetaRow = styled.div.attrs({ className: "u-mb8" })`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  flex-wrap: wrap;
`;

const ResultMetaText = styled.span.attrs({
  className: "bodytext-11 u-line-height-150",
})`
  color: var(--soremo-light-gray);
`;

const ResultActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const FooterActions = styled.div.attrs({ className: "u-mt16" })`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

// ─── コンポーネント ────────────────────────────────────

const MediaTranscriptionModal: FC<MediaTranscriptionModalProps> = ({
  fileId,
  fileName,
  onClose,
  onSeek,
}) => {
  const [mountedAt] = useState(() => Date.now());
  // 実行 / 再実行を押したクライアント時刻（fast ポーリング窓の基準）
  const [requestedAt, setRequestedAt] = useState<number | null>(null);
  // 直近のステータスに応じたポーリング制御（RTK Query の data 変化で再評価）
  const [isPolling, setIsPolling] = useState(false);
  // 実行条件（音声の種類 / 話者分離 / 語彙ヒント）。一括モーダルと共通の
  // TranscriptionOptionsForm が編集する。
  const [options, setOptions] = useState<TranscriptionOptionsValue>(
    DEFAULT_TRANSCRIPTION_OPTIONS,
  );
  /**
   * 実行済みの結果があっても、条件を選び直す画面を出しているか。
   * 「再実行」はすぐ走らせず、まずこの画面に戻して音声の種類を選べるようにする
   * （会話で取ってみて歌だったから歌唱で取り直す、という流れが多いため）。
   */
  const [isReconfiguring, setIsReconfiguring] = useState(false);

  const pollBaseMs = requestedAt ?? mountedAt;
  const pollingInterval =
    Date.now() - pollBaseMs > FAST_POLL_DURATION_MS
      ? SLOW_POLL_INTERVAL_MS
      : FAST_POLL_INTERVAL_MS;

  const { data, isLoading } = useGetTranscriptionQuery(fileId, {
    refetchOnMountOrArgChange: true,
    pollingInterval: isPolling ? pollingInterval : 0,
  });

  const [startTranscription, { isLoading: isStarting }] =
    useStartTranscriptionMutation();

  const info = data?.data;
  const status = info?.transcription_status ?? null;

  useEffect(() => {
    setIsPolling(status === "pending" || status === "processing");
  }, [status]);

  // ── 完了時: transcript_url の JSON（セグメント一覧）を取得 ──
  const transcriptUrl =
    status === "complete" ? (info?.transcript_url ?? null) : null;
  const [transcript, setTranscript] = useState<TranscriptJson | null>(null);
  const [transcriptError, setTranscriptError] = useState(false);

  useEffect(() => {
    if (!transcriptUrl) {
      setTranscript(null);
      return undefined;
    }
    let cancelled = false;
    setTranscriptError(false);
    fetch(transcriptUrl)
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json() as Promise<TranscriptJson>;
      })
      .then((json) => {
        if (!cancelled) setTranscript(json);
      })
      .catch(() => {
        if (!cancelled) setTranscriptError(true);
      });
    return () => {
      cancelled = true;
    };
  }, [transcriptUrl]);

  const segments: TranscriptSegment[] = transcript?.segments ?? [];

  // 全文コピー / 書き出し用のタイムスタンプ付きテキスト。
  // タイムスタンプはコメント本文のタグ（utils/timecode の [M:SS] 形式）に
  // 合わせ、コメント欄へそのままペーストするとクリックでシークできるようにする。
  // 1 時間超も h:mm:ss にせず総分数（例 [75:30]）で出す（タグ正規表現の互換のため）
  const timestampedText = useMemo(
    () =>
      segments
        .map((seg) => {
          const speaker = speakerLabel(seg.speaker);
          const prefix = speaker ? `${speaker} ` : "";
          return `[${formatTimecode(seg.start)}] ${prefix}${seg.text}`;
        })
        .join("\n"),
    [segments],
  );

  // ── アクション ──────────────────────────────────────
  const handleStart = useCallback(async () => {
    if (isStarting) return;
    try {
      await startTranscription({
        fileId,
        ...toTranscriptionOptions(options),
      }).unwrap();
      setRequestedAt(Date.now());
      setTranscript(null);
      setIsReconfiguring(false);
    } catch (err) {
      const message =
        (err as { data?: { message?: string } })?.data?.message ??
        "文字起こしの開始に失敗しました。時間をおいてお試しください。";
      toast.error(message);
    }
  }, [fileId, isStarting, options, startTranscription]);

  /** 「再実行」。前回の条件を初期値にして、選び直す画面へ戻す。 */
  const handleReconfigure = useCallback(() => {
    setOptions({
      mode: info?.mode ?? "conversation",
      promptHint: info?.prompt_hint ?? "",
      speakerLabels: Boolean(info?.has_speaker_labels),
    });
    setIsReconfiguring(true);
  }, [info?.mode, info?.prompt_hint, info?.has_speaker_labels]);

  const handleCopy = useCallback(async () => {
    if (!timestampedText) return;
    try {
      await navigator.clipboard.writeText(timestampedText);
      toast.success("タイムスタンプ付きで全文をコピーしました");
    } catch {
      toast.error("コピーに失敗しました");
    }
  }, [timestampedText]);

  const handleExport = useCallback(() => {
    if (segments.length === 0) return;
    const blob = new Blob([timestampedText], {
      type: "text/plain;charset=utf-8",
    });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    const base = (fileName ?? "transcript").replace(/\.[^.]+$/, "");
    anchor.download = `${base}_文字起こし.txt`;
    anchor.click();
    URL.revokeObjectURL(url);
  }, [segments, timestampedText, fileName]);

  const handleSeek = useCallback(
    (seg: TranscriptSegment) => {
      onSeek?.(seg.start);
    },
    [onSeek],
  );

  // ── 各状態の描画 ────────────────────────────────────
  /**
   * 実行条件（音声の種類・オプション）を選ぶ画面。
   * 未実行のときの開始画面と、結果から「再実行」で戻ってきたときに使う。
   *
   * @param canCancel 既に結果があり、選び直しをやめて戻れる場合は true
   */
  const renderStartScreen = (canCancel = false) => (
    <CenterState>
      <StateText>
        {canCancel
          ? "音声の種類を選び直して、もう一度文字起こしします。\n前の結果は新しい結果で置き換わります。"
          : "AI がメディアの音声を解析し、タイムスタンプ付きのテキストに変換します。\n結果はこのメディアを閲覧できるメンバー全員に共有されます。"}
      </StateText>
      <StateSubText>
        所要時間の目安はメディアの長さの半分程度です。
      </StateSubText>
      <TranscriptionOptionsForm
        value={options}
        onChange={setOptions}
        disabled={isStarting}
      />
      <FooterActions>
        {canCancel && (
          <Button
            size="small"
            theme="secondary"
            variant="text"
            width="fit"
            leftIcon="arrow_back"
            disabled={isStarting}
            onClick={() => setIsReconfiguring(false)}
          >
            前の結果に戻る
          </Button>
        )}
        <Button
          size="small"
          theme="primary"
          variant="filled"
          width="fit"
          leftIcon={isStarting ? "hourglass_empty" : "speech_to_text"}
          disabled={isStarting}
          onClick={handleStart}
        >
          {isStarting
            ? "送信中…"
            : canCancel
              ? "この条件で取り直す"
              : "文字起こしを開始"}
        </Button>
      </FooterActions>
    </CenterState>
  );

  const renderBody = () => {
    if (isLoading || status == null) {
      return (
        <CenterState>
          <InlineLoading />
        </CenterState>
      );
    }

    // 実行中は条件を変えられない（下の実行中表示を優先する）
    const isRunning = status === "pending" || status === "processing";
    if (isReconfiguring && !isRunning) {
      // 既に結果があるなら、選び直しをやめて結果へ戻れるようにする
      return renderStartScreen(status !== "none");
    }

    if (status === "pending" || status === "processing") {
      return (
        <CenterState>
          <InlineLoading />
          <StateText>文字起こしを実行中です…</StateText>
          <StateSubText>
            {
              "所要時間の目安はメディアの長さの半分程度です。\nこの画面を閉じても処理は続行されます。"
            }
          </StateSubText>
        </CenterState>
      );
    }

    if (status === "failed") {
      return (
        <CenterState>
          <StateText>
            {info?.error_message || "文字起こしに失敗しました"}
          </StateText>
          <FooterActions>
            <Button
              size="small"
              theme="secondary"
              variant="outlined"
              width="fit"
              leftIcon="refresh"
              disabled={isStarting}
              onClick={handleReconfigure}
            >
              再実行
            </Button>
          </FooterActions>
        </CenterState>
      );
    }

    if (status === "complete") {
      if (transcriptError) {
        return (
          <CenterState>
            <StateText>文字起こし結果の取得に失敗しました</StateText>
          </CenterState>
        );
      }
      if (!transcript) {
        return (
          <CenterState>
            <InlineLoading />
          </CenterState>
        );
      }
      if (segments.length === 0) {
        return (
          <CenterState>
            <StateText>音声からテキストを検出できませんでした</StateText>
            <FooterActions>
              <Button
                size="small"
                theme="secondary"
                variant="outlined"
                width="fit"
                leftIcon="refresh"
                disabled={isStarting}
                onClick={handleReconfigure}
              >
                再実行
              </Button>
            </FooterActions>
          </CenterState>
        );
      }

      let prevSpeaker: string | null = null;
      return (
        <>
          <ResultMetaRow>
            <ResultMetaText>
              {info?.completed_at && (
                <DateDisplay
                  date={info.completed_at}
                  type={DATE_DISPLAY_TYPE.SHORT_DATE_TIME}
                />
              )}
              {info?.requested_by_name && ` ${info.requested_by_name} が実行`}
            </ResultMetaText>
            <ResultActions>
              <Button
                size="small"
                theme="secondary"
                variant="text"
                width="fit"
                leftIcon="content_copy"
                onClick={handleCopy}
              >
                全文コピー
              </Button>
              <Button
                size="small"
                theme="secondary"
                variant="text"
                width="fit"
                leftIcon="download"
                onClick={handleExport}
              >
                書き出し
              </Button>
              <Button
                size="small"
                theme="secondary"
                variant="text"
                width="fit"
                leftIcon="refresh"
                disabled={isStarting}
                onClick={handleReconfigure}
              >
                再実行
              </Button>
            </ResultActions>
          </ResultMetaRow>
          <SegmentList>
            {segments.map((seg) => {
              const label = speakerLabel(seg.speaker);
              const showSpeaker = label != null && seg.speaker !== prevSpeaker;
              prevSpeaker = seg.speaker;
              return (
                <SegmentRow
                  key={seg.id}
                  $clickable={onSeek != null}
                  onClick={() => handleSeek(seg)}
                >
                  <SegmentTime>{formatTimestamp(seg.start)}</SegmentTime>
                  {label != null && (
                    <SegmentSpeaker>{showSpeaker ? label : ""}</SegmentSpeaker>
                  )}
                  <SegmentText>{seg.text}</SegmentText>
                </SegmentRow>
              );
            })}
          </SegmentList>
        </>
      );
    }

    return renderStartScreen();
  };

  return (
    <Modal
      onClose={onClose}
      titleSize="compact"
      title="文字起こし"
      description={fileName}
      descriptionTone="subtle"
      size={status === "complete" ? "default" : "auto"}
    >
      {renderBody()}
    </Modal>
  );
};

export default MediaTranscriptionModal;
